import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 9: Nervous System
 * Vertical spine-like structure (vagus nerve) with branching nodes
 * Electrical pulses travel through network on audio beats
 */
export default function NeuralNetwork({ audioData, isActive, position = [0, 0, 0] }) {
  const spineRef = useRef()
  const nodesRef = useRef([])
  const pulsesRef = useRef([])

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Create spine path (vertical with slight curve)
  const spinePath = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, -3, 0),
      new THREE.Vector3(0.2, -2, 0),
      new THREE.Vector3(-0.1, -1, 0),
      new THREE.Vector3(0.1, 0, 0),
      new THREE.Vector3(-0.15, 1, 0),
      new THREE.Vector3(0.1, 2, 0),
      new THREE.Vector3(0, 3, 0)
    ])
    return curve
  }, [])

  // Node positions along spine
  const nodePositions = useMemo(() => {
    const positions = []
    for (let i = 0; i <= 12; i++) {
      const t = i / 12
      const point = spinePath.getPoint(t)
      positions.push(point)
    }
    return positions
  }, [spinePath])

  // Pulse state (position along spine, 0-1)
  const pulseState = useRef(0)
  const lastBeat = useRef(0)

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    // Spine gentle sway
    if (spineRef.current) {
      spineRef.current.rotation.z = Math.sin(time * 0.5) * 0.1
    }

    // Animate nodes
    nodesRef.current.forEach((node, i) => {
      if (!node) return

      // Idle pulse
      const idlePulse = Math.sin(time * 0.8 + i * 0.5) * 0.05
      node.scale.setScalar(1 + idlePulse)

      // Emissive intensity
      const baseIntensity = 0.5 + Math.sin(time * 0.6 + i * 0.3) * 0.3
      node.material.emissiveIntensity = baseIntensity + amplitude * 0.5

      // React to nearby pulse
      const nodePulseT = i / 12
      const pulseDist = Math.abs(pulseState.current - nodePulseT)
      if (pulseDist < 0.1) {
        const pulseIntensity = 1 - pulseDist / 0.1
        node.scale.setScalar(1.5 + pulseIntensity * 0.5)
        node.material.emissiveIntensity = 2 + pulseIntensity * 2
      }
    })

    // Trigger pulse on beat
    if (beat > 0.5 && time - lastBeat.current > 0.3) {
      pulseState.current = 0
      lastBeat.current = time
    }

    // Advance pulse
    if (pulseState.current < 1) {
      pulseState.current += delta * 0.2 // Travel time ~2 seconds
    }

    // Animate pulse spheres
    pulsesRef.current.forEach((pulse, i) => {
      if (!pulse) return

      const pulseT = (pulseState.current + i * 0.2) % 1
      const point = spinePath.getPoint(pulseT)

      pulse.position.copy(point)

      // Fade out pulse as it travels
      const opacity = Math.max(0, 1 - pulseT)
      pulse.material.opacity = opacity
      pulse.scale.setScalar(0.3 + (1 - pulseT) * 0.3)
    })
  })

  return (
    <group position={position}>
      {/* Main spine structure */}
      <group ref={spineRef}>
        {/* Spine tube */}
        <mesh>
          <tubeGeometry args={[spinePath, 64, 0.08, 8, false]} />
          <meshStandardMaterial
            color="#FF6B6B"
            emissive="#FF6B6B"
            emissiveIntensity={0.6}
            metalness={0.4}
            roughness={0.3}
          />
        </mesh>

        {/* Nodes along spine */}
        {nodePositions.map((pos, i) => (
          <mesh
            key={i}
            position={[pos.x, pos.y, pos.z]}
            ref={(el) => (nodesRef.current[i] = el)}
          >
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshStandardMaterial
              color="#FF4444"
              emissive="#FF8888"
              emissiveIntensity={0.5}
              transparent
              opacity={0.9}
            />

            {/* Branch connections (every other node) */}
            {i % 2 === 0 && (
              <>
                <mesh position={[0.5, 0, 0]}>
                  <cylinderGeometry args={[0.03, 0.03, 0.5, 8]} />
                  <meshStandardMaterial
                    color="#FF6B6B"
                    emissive="#FF6B6B"
                    emissiveIntensity={0.4}
                  />
                </mesh>
                <mesh position={[-0.5, 0, 0]}>
                  <cylinderGeometry args={[0.03, 0.03, 0.5, 8]} />
                  <meshStandardMaterial
                    color="#FF6B6B"
                    emissive="#FF6B6B"
                    emissiveIntensity={0.4}
                  />
                </mesh>
              </>
            )}
          </mesh>
        ))}

        {/* Electrical pulses */}
        {[0, 1, 2].map((i) => (
          <mesh
            key={`pulse-${i}`}
            ref={(el) => (pulsesRef.current[i] = el)}
          >
            <sphereGeometry args={[0.3, 16, 16]} />
            <meshBasicMaterial
              color="#FFFF00"
              transparent
              opacity={0}
              blending={THREE.AdditiveBlending}
            />
          </mesh>
        ))}
      </group>

      {/* Ambient light */}
      <pointLight position={[0, 0, 0]} intensity={1} color="#FF6B6B" distance={10} />
    </group>
  )
}
